import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import mesApi from "../../../Api/mes.api";
import { ProcessParameter, TimelineBatch } from "../../../types/mes";
import BatchMaterialConfirm from "../../../components/MES/BatchMaterialConfirm";
import {
  PROCESS_PHASE_GROUPS,
  buildProcessForm,
  derivedDisplayValue,
  processFieldsForGroups,
  processPayloadFromForm,
} from "./processFields";

/**
 * Procesni podaci jedne zamjese, sve faze — uređivanje izvan ekrana vezanog na
 * jedan proizvodni dan.
 *
 * Polja dolaze iz istog kataloga (processFields.ts) koji renderira tablet nalog
 * i ekran "Procesni podaci", pa se ista zamjesa svugdje uređuje istim setom
 * polja. Uz parametre je i panel sirovina, jer ispravak procesnih podataka
 * gotovo uvijek ide zajedno s ispravkom onoga što je u zamjes stvarno ušlo.
 */

interface Props {
  batch: TimelineBatch;
  /** Datum proizvodnje (YYYY-MM-DD) koji se piše na procesni red. */
  productionDate: string;
  /** Postojeći procesni red, ako je već zabilježen. */
  parameter?: ProcessParameter | null;
  onSaved?: () => void;
}

const ALL_FIELDS = processFieldsForGroups(PROCESS_PHASE_GROUPS);

export default function BatchProcessDataEditor({ batch, productionDate, parameter, onSaved }: Props) {
  const [form, setForm] = useState<Record<string, string>>(() => buildProcessForm(parameter ?? {}, ALL_FIELDS));
  const [saving, setSaving] = useState(false);
  const [panel, setPanel] = useState<"params" | "materials">("params");

  const reset = useCallback(() => {
    const next = buildProcessForm(parameter ?? {}, ALL_FIELDS);
    if (!next.DoughMassKg && batch.FinalMassKg) next.DoughMassKg = String(batch.FinalMassKg);
    setForm(next);
  }, [parameter, batch.FinalMassKg]);

  useEffect(() => {
    reset();
  }, [reset]);

  const setField = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        MixCode: batch.MixCode,
        TimelineBatch: batch.ID,
        Product: batch.ProductID,
        ProductionDate: productionDate,
        ...processPayloadFromForm(form, ALL_FIELDS),
      };
      if (parameter?.ID) await mesApi.updateProcessParameter(parameter.ID, payload);
      else await mesApi.createProcessParameter(payload);
      toast.success("Procesni podaci spremljeni");
      onSaved?.();
    } catch (err: any) {
      toast.error("Greška pri spremanju: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="batch-process-editor">
      <div className="kpi-tabs fq-analysis-tabs" style={{ marginBottom: "0.75rem" }}>
        <button
          type="button"
          className={`kpi-tab ${panel === "params" ? "active" : ""}`}
          onClick={() => setPanel("params")}
        >
          Procesni parametri
        </button>
        <button
          type="button"
          className={`kpi-tab ${panel === "materials" ? "active" : ""}`}
          onClick={() => setPanel("materials")}
        >
          Sirovine i lotovi
        </button>
      </div>

      {panel === "materials" ? (
        <BatchMaterialConfirm batchId={batch.ID} onChanged={onSaved} />
      ) : (
        <>
          {PROCESS_PHASE_GROUPS.map((group) => (
            <div key={group.code}>
              <h5 style={{ margin: "1rem 0 0.5rem" }}>{group.label}</h5>
              {group.fields.some((f) => f.type === "textarea") ? (
                group.fields.map((field) => (
                  <div className="form-group" key={field.key}>
                    <label>{field.label}</label>
                    <textarea
                      value={form[field.key] || ""}
                      onChange={(e) => setField(field.key, e.target.value)}
                    />
                  </div>
                ))
              ) : (
                <div className="form-grid-2">
                  {group.fields.map((field) => (
                    <div className="form-group" key={field.key}>
                      <label>{field.label}</label>
                      {field.derived ? (
                        <input type="text" value={derivedDisplayValue(form, field.key)} readOnly disabled />
                      ) : (
                        <input
                          type={field.type === "textarea" ? "text" : field.type}
                          step={field.step}
                          value={form[field.key] || ""}
                          onChange={(e) => setField(field.key, e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: "1rem" }}>
            <button type="button" className="btn-secondary" onClick={reset} disabled={saving}>
              Vrati spremljeno
            </button>
            <button type="button" className="btn-submit" onClick={save} disabled={saving}>
              {saving ? "Spremanje…" : "Spremi procesne podatke"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
